import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';

import { ETPayButton } from '../../../../../components/button';
import { FormInput } from '../../../../../components/form/form-input';
import { Options, Select } from '../../../../../components/form/select';
import { Modal } from '../../../../../components/modal';
import { RateLimitError } from '../../../../../errors/RateLimitError';
import { useAuth } from '../../../../../hooks/useAuth';
import { useFloatingAlert } from '../../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../../hooks/useMerchant';
import { UsersServices } from '../../../../../services/users';
import { getRoleIdByLabel, getRoleLabelById, reduceRoles } from '../../../../../utils/common';
import { InviteNewUserSchema } from '../../../../../utils/validations';
import { Item } from '../../../../payment-links-3/components/modal-payment/components/detail';
import { FeaturesSection } from '../../features';
import { ErrorLimitModal } from '../error-limit';
import {
	ButtonContainer,
	FeatureSectionTitle,
	FormGroup,
	MainSection,
	Subtitle,
	SummarySection,
} from '../styles';

interface NewUserModalProps {
	show: boolean;
	handleCloseModal: () => void;
	refetch?: () => void;
}

interface NewUserValues {
	name: string;
	lastName: string;
	email: string;
	companyName: string;
}

const defaultValues: NewUserValues = {
	name: '',
	lastName: '',
	email: '',
	companyName: '',
};

const NewUserModal = ({ show, handleCloseModal, refetch }: NewUserModalProps) => {
	const { user } = useAuth();
	const { merchantSelected } = useMerchant();
	const { handleShowAlert } = useFloatingAlert();
	const [loading, setLoading] = useState(false);
	const [showError, setShowError] = useState(false);
	const [formValues, setFormValues] = useState<NewUserValues>(defaultValues);
	const [optionsSelected, setOptionsSelected] = useState({ role: 0, merchant: 0 });

	const createMerchantOptions = () => {
		const merchantOptions: Options[] = [{ label: 'Selecciona un comercio', value: '', show: true }];
		const merchantList = [
			{
				label: merchantSelected?.name,
				value: merchantSelected?.id,
			},
		];
		return merchantOptions.concat(merchantList as Options[]);
	};

	const handleSubmit = async (values: NewUserValues) => {
		setLoading(true);
		try {
			const { name, lastName, email } = values;
			await UsersServices.inviteUser({
				merchantCode: merchantSelected?.code as string,
				names: name,
				lastNames: lastName,
				email,
				roleId: optionsSelected.role,
				merchantId: optionsSelected.merchant,
			});
			handleShowAlert({
				message: 'La invitación se ha enviado correctamente',
				type: 'success',
			});
			refetch && refetch();
			handleCloseModal();
		} catch (error) {
			if (error instanceof RateLimitError) {
				handleShowAlert({ type: 'error', message: error.msg });
				setShowError(true);
				setFormValues(values);
			} else {
				handleShowAlert({ type: 'error', message: 'Error al invitar usuario' });
			}
		} finally {
			setLoading(false);
		}
	};

	const items: Item[] = [
		{ title: 'Nombre', value: formValues.name },
		{ title: 'Apellidos', value: formValues.lastName },
		{ title: 'Correo electrónico', value: formValues.email },
		{ title: 'Comercio', value: merchantSelected?.name as string },
		{
			title: 'Rol',
			value: getRoleLabelById(
				optionsSelected.role,
				merchantSelected?.role_id as number,
				user?.email.includes('@etpay'),
			),
		},
	];

	useEffect(() => {
		setOptionsSelected({
			...optionsSelected,
			role: getRoleIdByLabel('Propietario', merchantSelected?.role_id as number),
			merchant: merchantSelected?.id as number,
		});
	}, []);

	const renderModals = () => {
		if (showError) {
			return <ErrorLimitModal show={show} onClose={handleCloseModal} items={items} />;
		} else {
			return (
				<Modal title="Invitar usuario" show={show} onClose={handleCloseModal} type="large">
					<MainSection>
						<Subtitle>
							Le enviaremos un correo electrónico de invitación a la persona para que acceda a la
							cuenta.
						</Subtitle>

						<Formik
							initialValues={defaultValues}
							onSubmit={handleSubmit}
							validationSchema={InviteNewUserSchema}
						>
							{({ isValid, dirty, validateForm }) => (
								<Form>
									<FormGroup>
										<FormInput type="text" label="Nombre(s)" name="name" />
										<FormInput type="text" label="Apellidos" name="lastName" />
									</FormGroup>

									<FormInput type="text" label="Correo electrónico" name="email" />

									<Select
										name="merchants"
										size="large"
										orientation="bottom"
										options={createMerchantOptions() || []}
										defaultOption={merchantSelected?.id}
										disabled
										onChange={() => []}
									/>
									<Select
										titleLabel="Selecciona un rol"
										name="roles"
										size="large"
										orientation="bottom"
										sizeHeight="min"
										options={reduceRoles(
											merchantSelected?.role_id as number,
											user?.email.includes('@etpay'),
										)}
										defaultOption={
											reduceRoles(
												merchantSelected?.role_id as number,
												user?.email.includes('@etpay'),
											).find((role) => role.value === merchantSelected?.role_id)?.value as number
										}
										onChange={(e) => {
											setOptionsSelected({ ...optionsSelected, role: e.value as number });
											validateForm();
										}}
									/>

									<ButtonContainer>
										<ETPayButton
											title={loading ? 'Enviando...' : 'Enviar invitación'}
											loading={loading}
											color="secondary"
											variant="effect"
											type="submit"
											disabled={
												!isValid || !dirty || !optionsSelected.role || !optionsSelected.merchant
											}
										/>
									</ButtonContainer>
								</Form>
							)}
						</Formik>
					</MainSection>

					<SummarySection>
						<FeatureSectionTitle>Este usuario tendrá permisos para:</FeatureSectionTitle>
						<FeaturesSection
							role={getRoleLabelById(
								optionsSelected.role,
								merchantSelected?.role_id as number,
								user?.email.includes('@etpay'),
							)}
						/>
					</SummarySection>
				</Modal>
			);
		}
	};

	return <>{renderModals()}</>;
};

export { NewUserModal };
